import type { FixingSpec } from "@/config/designer";
import { getFixingSpec } from "@/config/designer";
import type { VoxelField } from "@/core/field/VoxelField";
import type { ExtractedMesh } from "@/core/mesh/surfaceNets";
import type { Fixing } from "./types";

type Vec3 = readonly [number, number, number];

/**
 * A screw follows the local surface normal, capped to a structurally sensible
 * lean so it still reaches the flat wall-facing plane. The countersink then
 * gets a shallow flared shoulder which blends a curved foam surface into the
 * screw seat instead of leaving a hard, lopsided rim.
 */
export interface HoleProfile {
  /** Compatibility/world-space measurements used by diagnostics and tests. */
  mouthY: number;
  coneBaseY: number;
  bottomY: number;
  topY: number;
  shaftRadius: number;
  countersinkRadius: number;
  blendRadius: number;
  hasCountersink: boolean;

  /** World-space point at the centre of the visible surface. */
  surfacePoint: Vec3;
  /** Unit vector from the wall-facing rear plane towards the screw head. */
  axis: Vec3;
  /** Axial distances from surfacePoint. Negative values travel into the hold. */
  rearT: number;
  coneBaseT: number;
  mouthT: number;
  topT: number;
}

export interface PlacementCheck {
  valid: boolean;
  reason?: string;
  surfaceY: number | null;
  columnThickness: number;
  surfacePoint: Vec3 | null;
  axis: Vec3;
  rearPoint: Vec3 | null;
}

export interface FixingOrientation {
  surfacePoint: Vec3;
  axis: Vec3;
  rearPoint: Vec3;
}

const UP: Vec3 = [0, 1, 0];
const OVERSHOOT = 2.0;
const MIN_SHAFT_BELOW_CONE = 2.0;

export function countersinkDepth(spec: FixingSpec): number {
  const half = (spec.countersinkAngle * Math.PI) / 360;
  const tan = Math.tan(half);
  const radial = (spec.countersinkDiameter - spec.shaftDiameter) / 2;
  return tan > 1e-6 ? radial / tan : radial;
}

/** Resolve the best surface-normal angle while keeping the rear exit usable. */
export function resolveFixingOrientation(
  field: VoxelField,
  spec: FixingSpec,
  x: number,
  z: number,
): FixingOrientation | null {
  const surfaceY = field.surfaceHeightAt(x, z);
  if (surfaceY === null) return null;

  const raw = field.gradient(x, surfaceY, z, [0, 1, 0]);
  const horizontal = Math.hypot(raw[0], raw[2]);
  const maxTilt = (spec.maxTiltDegrees * Math.PI) / 180;
  const rawTilt = Math.atan2(horizontal, Math.max(raw[1], 0.001));
  const tilt = Math.min(rawTilt, maxTilt);
  const horizontalScale = horizontal > 1e-6 ? Math.sin(tilt) / horizontal : 0;
  const axis: Vec3 = [
    raw[0] * horizontalScale,
    Math.cos(tilt),
    raw[2] * horizontalScale,
  ];
  const toRear = -surfaceY / axis[1];
  const surfacePoint: Vec3 = [x, surfaceY, z];
  const rearPoint: Vec3 = [
    x + axis[0] * toRear,
    0,
    z + axis[2] * toRear,
  ];
  return { surfacePoint, axis, rearPoint };
}

/** Is this a sane place to put a fixing? Drives the red/green hover preview. */
export function checkPlacement(
  field: VoxelField,
  spec: FixingSpec,
  x: number,
  z: number,
): PlacementCheck {
  const surfaceY = field.surfaceHeightAt(x, z);
  const columnThickness = field.columnThicknessAt(x, z);
  const orientation = resolveFixingOrientation(field, spec, x, z);
  const base = {
    surfaceY,
    columnThickness,
    surfacePoint: orientation?.surfacePoint ?? null,
    axis: orientation?.axis ?? UP,
    rearPoint: orientation?.rearPoint ?? null,
  };

  if (x < 0 || z < 0 || x > field.width || z > field.depth) {
    return { ...base, valid: false, reason: "Outside the block" };
  }
  if (!orientation || surfaceY === null || columnThickness <= 0) {
    return { ...base, valid: false, reason: "No material here" };
  }
  if (columnThickness < spec.minColumnHeight) {
    return { ...base, valid: false, reason: `Needs ${spec.minColumnHeight} mm of material` };
  }

  // Both the blended surface shoulder and the shifted rear exit need material
  // around them. This also prevents a steeply tilted shaft escaping a side.
  const shoulder = spec.countersinkDiameter / 2 + spec.surfaceBlendRadius + spec.edgeMargin;
  const rearClearance = spec.shaftDiameter / 2 + spec.edgeMargin;
  if (!insideFootprint(orientation.rearPoint[0], orientation.rearPoint[2], rearClearance, field)) {
    return { ...base, valid: false, reason: "Angle exits too close to the rear edge" };
  }
  for (let a = 0; a < 12; a++) {
    const angle = (a / 12) * Math.PI * 2;
    const px = x + Math.cos(angle) * shoulder;
    const pz = z + Math.sin(angle) * shoulder;
    if (field.columnThicknessAt(px, pz) <= 0) {
      return { ...base, valid: false, reason: "Too close to the edge" };
    }
  }
  return { ...base, valid: true };
}

function insideFootprint(x: number, z: number, margin: number, field: VoxelField): boolean {
  return x >= margin && z >= margin && x <= field.width - margin && z <= field.depth - margin;
}

export function buildHoleProfile(
  field: VoxelField,
  spec: FixingSpec,
  x: number,
  z: number,
): HoleProfile | null {
  const orientation = resolveFixingOrientation(field, spec, x, z);
  if (!orientation) return null;
  const { surfacePoint, axis } = orientation;

  const shaftRadius = spec.shaftDiameter / 2;
  const countersinkRadius = spec.countersinkDiameter / 2;
  const blendRadius = countersinkRadius + spec.surfaceBlendRadius;
  const depth = countersinkDepth(spec);

  const rearSurfaceT = -surfacePoint[1] / axis[1];
  const rearT = (-OVERSHOOT - surfacePoint[1]) / axis[1];
  let mouthT = -spec.headClearance;
  let coneBaseT = mouthT - depth;
  let hasCountersink = true;

  const minimumConeBaseT = rearSurfaceT + MIN_SHAFT_BELOW_CONE;
  if (coneBaseT < minimumConeBaseT) {
    coneBaseT = minimumConeBaseT;
    if (mouthT <= coneBaseT + 0.2) {
      mouthT = coneBaseT;
      hasCountersink = false;
    }
  }

  const topT = OVERSHOOT;
  return {
    mouthY: surfacePoint[1] + axis[1] * mouthT,
    coneBaseY: surfacePoint[1] + axis[1] * coneBaseT,
    bottomY: -OVERSHOOT,
    topY: surfacePoint[1] + axis[1] * topT,
    shaftRadius,
    countersinkRadius,
    blendRadius,
    hasCountersink,
    surfacePoint,
    axis,
    rearT,
    coneBaseT,
    mouthT,
    topT,
  };
}

/** Closed, watertight solid of revolution aligned to the chosen screw axis. */
export function buildHoleSolid(
  profile: HoleProfile,
  _x: number,
  _z: number,
  segments: number,
): ExtractedMesh {
  const rings: Array<{ t: number; r: number }> = [];
  const push = (t: number, r: number): void => {
    const last = rings[rings.length - 1];
    if (last && Math.abs(last.t - t) < 1e-6 && Math.abs(last.r - r) < 1e-6) return;
    rings.push({ t, r });
  };

  push(profile.rearT, profile.shaftRadius);
  if (profile.hasCountersink) {
    push(profile.coneBaseT, profile.shaftRadius);
    push(profile.mouthT, profile.countersinkRadius);
    // Two eased steps form a gentle, printable transition from the curved
    // foam into the countersink instead of a sharp annular notch.
    push(profile.mouthT * 0.45, lerp(profile.countersinkRadius, profile.blendRadius, 0.68));
    push(0, profile.blendRadius);
    push(profile.topT, profile.blendRadius);
  } else {
    const relievedShaft = profile.shaftRadius + (profile.blendRadius - profile.countersinkRadius);
    push(0, relievedShaft);
    push(profile.topT, relievedShaft);
  }

  const [u, v] = perpendicularBasis(profile.axis);
  const ringCount = rings.length;
  const positions = new Float32Array((ringCount * segments + 2) * 3);
  const indices: number[] = [];

  for (let r = 0; r < ringCount; r++) {
    const ring = rings[r] as { t: number; r: number };
    for (let s = 0; s < segments; s++) {
      const angle = (s / segments) * Math.PI * 2;
      const cos = Math.cos(angle) * ring.r;
      const sin = Math.sin(angle) * ring.r;
      const p = (r * segments + s) * 3;
      positions[p] = profile.surfacePoint[0] + profile.axis[0] * ring.t + u[0] * cos + v[0] * sin;
      positions[p + 1] = profile.surfacePoint[1] + profile.axis[1] * ring.t + u[1] * cos + v[1] * sin;
      positions[p + 2] = profile.surfacePoint[2] + profile.axis[2] * ring.t + u[2] * cos + v[2] * sin;
    }
  }

  const bottomCentre = ringCount * segments;
  const topCentre = bottomCentre + 1;
  writeAxisPoint(positions, bottomCentre, profile, (rings[0] as { t: number }).t);
  writeAxisPoint(positions, topCentre, profile, (rings[ringCount - 1] as { t: number }).t);

  for (let r = 0; r < ringCount - 1; r++) {
    for (let s = 0; s < segments; s++) {
      const next = (s + 1) % segments;
      const a = r * segments + s;
      const b = r * segments + next;
      const c = (r + 1) * segments + next;
      const d = (r + 1) * segments + s;
      indices.push(a, d, c, a, c, b);
    }
  }
  const top = (ringCount - 1) * segments;
  for (let s = 0; s < segments; s++) {
    const next = (s + 1) % segments;
    indices.push(bottomCentre, s, next);
    indices.push(topCentre, top + next, top + s);
  }
  return { positions, indices: new Uint32Array(indices) };
}

function perpendicularBasis(axis: Vec3): [Vec3, Vec3] {
  const length = Math.hypot(axis[1], axis[0]) || 1;
  const u: Vec3 = [axis[1] / length, -axis[0] / length, 0];
  const v: Vec3 = [
    u[1] * axis[2] - u[2] * axis[1],
    u[2] * axis[0] - u[0] * axis[2],
    u[0] * axis[1] - u[1] * axis[0],
  ];
  return [u, v];
}

function writeAxisPoint(
  positions: Float32Array,
  index: number,
  profile: HoleProfile,
  t: number,
): void {
  positions[index * 3] = profile.surfacePoint[0] + profile.axis[0] * t;
  positions[index * 3 + 1] = profile.surfacePoint[1] + profile.axis[1] * t;
  positions[index * 3 + 2] = profile.surfacePoint[2] + profile.axis[2] * t;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function buildFixingCutter(
  field: VoxelField,
  fixing: Fixing,
  segments: number,
): ExtractedMesh | null {
  const spec = getFixingSpec(fixing.typeId);
  const profile = buildHoleProfile(field, spec, fixing.x, fixing.z);
  return profile ? buildHoleSolid(profile, fixing.x, fixing.z, segments) : null;
}
